import {VAELTHAR,VAELTHAR_ID,VAELTHAR_SKILLS,type VaeltharGame} from './vaelthar.js';
export type VaeltharGoal='guard'|'heal-ally'|'control-threat'|'research'|'travel'|'recover'|'defend-self';
export type VaeltharMemoryKind='battle'|'ally'|'discovery'|'loss'|'dialogue'|'objective';
export interface VaeltharMemory {kind:VaeltharMemoryKind;subject:string;detail:string;atWorldSec:number;salience:number;}
export interface VaeltharMind {
 schema:1;characterId:typeof VAELTHAR_ID;worldSec:number;lastDecisionWorldSec:number;goal:VaeltharGoal;
 mana:number;health:number;fatigue:number;curiosity:number;trust:Record<string,number>;
 discovered:Record<string,number>;memories:VaeltharMemory[];cooldowns:Record<string,number>;researchProgress:number;
}
export interface VaeltharPerception {
 game:VaeltharGame;worldSec:number;hostiles:number;nearestHostileMeters:number;immediateThreat:number;
 alliedLowHealth:number;civiliansAtRisk:number;unknownStarSignals:number;canTravel:boolean;
 healthFraction:number;manaFraction:number;specialReady?:boolean;ultimateReady?:boolean;attackReady?:boolean;nearbyAllyId?:string;targetId?:string;regionId?:string;
}
export interface VaeltharDecision {goal:VaeltharGoal;skillId:string|null;targetId:string|null;reason:string;confidence:number;}
const clamp=(v:number,min=0,max=1)=>Math.min(max,Math.max(min,Number.isFinite(v)?v:min));
const SKILLS=Object.fromEntries(VAELTHAR_SKILLS.map(s=>[s.id,s])) as Record<string,(typeof VAELTHAR_SKILLS)[number]>;
const ready=(m:VaeltharMind,p:VaeltharPerception,id:string)=>{
 const skill=SKILLS[id];
 if(!skill||!skill.gameplay.includes(p.game)||m.mana<skill.manaCost||p.worldSec<(m.cooldowns[id]??0))return false;
 if(p.attackReady===false&&['light','heavy','special','ultimate'].includes(skill.slot))return false;
 if(p.specialReady===false&&['special','ultimate'].includes(skill.slot))return false;
 if(p.ultimateReady===false&&skill.slot==='ultimate')return false;
 return true;
};
function select(m:VaeltharMind,p:VaeltharPerception,goal:VaeltharGoal):VaeltharDecision{
 const usable=(ids:string[])=>ids.find(id=>ready(m,p,id))??null;
 let skill:string|null=null,reason='';
 if(goal==='heal-ally'){skill=usable(['astrolabe-guard','stellar-convergence']);reason='protect endangered ally';}
 else if(goal==='guard'){skill=usable(['observatory-ward','astrolabe-guard','orbital-seal']);reason='protect civilians before offense';}
 else if(goal==='control-threat'){skill=usable(['astral-cathedral','orbital-seal','constellation-breaker','astral-thread']);reason='interrupt hostile advance';}
 else if(goal==='defend-self'){skill=usable(['star-step','astrolabe-guard','astral-thread']);reason='restore safe distance';}
 else if(goal==='recover'){skill=usable(['stellar-convergence','astrolabe-guard']);reason='preserve focus and energy';}
 else if(goal==='research'){skill=usable(['celestial-course','observatory-ward']);reason='map anomalous star signals';}
 else{skill=usable(['celestial-course']);reason='resume astral expedition';}
 return {goal,skillId:skill,targetId:goal==='heal-ally'?p.nearbyAllyId??null:p.targetId??null,reason,confidence:.8};
}
export function createVaeltharMind(worldSec=0):VaeltharMind{
 return {schema:1,characterId:VAELTHAR_ID,worldSec:Number.isFinite(worldSec)?Math.max(0,worldSec):0,lastDecisionWorldSec:-1,
  goal:'research',mana:100,health:1,fatigue:0,curiosity:VAELTHAR.genetics.psyche.curiosity,
  trust:{},discovered:{},memories:[],cooldowns:{},researchProgress:0};
}
export function vaeltharRemember(m:VaeltharMind,event:VaeltharMemory):VaeltharMind{
 const normalized={...event,subject:String(event.subject).slice(0,90),detail:String(event.detail).slice(0,240),
  salience:clamp(event.salience),atWorldSec:Math.max(0,Math.min(event.atWorldSec,m.worldSec+1))};
 const memories=[...m.memories,normalized].sort((a,b)=>b.salience-a.salience||b.atWorldSec-a.atWorldSec)
  .slice(0,VAELTHAR.mind.memoryMax).sort((a,b)=>a.atWorldSec-b.atWorldSec);
 return {...m,memories};
}
export function vaeltharTrust(m:VaeltharMind,allyId:string,change:number):VaeltharMind{
 if(!allyId||allyId===VAELTHAR_ID)return m;
 return {...m,trust:{...m.trust,[allyId]:clamp((m.trust[allyId]??VAELTHAR.mind.social.trustStarts)+change)}};
}
export function vaeltharObserve(m:VaeltharMind,p:VaeltharPerception){
 const dt=clamp(p.worldSec-m.worldSec,0,10),mana=clamp(Math.min(m.mana,p.manaFraction*100)+dt*(m.goal==='recover'?6:2),0,100);
 const next:VaeltharMind={...m,worldSec:Math.max(m.worldSec,p.worldSec),mana,health:clamp(p.healthFraction),
  fatigue:clamp(m.fatigue+dt*(p.hostiles>0?.012:-.008),0,1)};
 if(p.regionId&&p.unknownStarSignals>0.3&&m.discovered[p.regionId]===undefined){
  next.discovered={...next.discovered,[p.regionId]:next.worldSec};
  return vaeltharRemember(next,{kind:'discovery',subject:p.regionId,detail:'Star anomaly mapped',salience:.86,atWorldSec:next.worldSec});
 }
 return next;
}
export function vaeltharDecide(m:VaeltharMind,p:VaeltharPerception):{state:VaeltharMind;decision:VaeltharDecision}{
 const next=vaeltharObserve(m,p);
 let goal:VaeltharGoal;
 if(p.civiliansAtRisk>0)goal='guard';
 else if(p.alliedLowHealth>.55)goal='heal-ally';
 else if(p.healthFraction<.36||p.immediateThreat>.85)goal='defend-self';
 else if(p.hostiles>0&&p.nearestHostileMeters<23)goal='control-threat';
 else if(p.manaFraction<.24||next.fatigue>.82)goal='recover';
 else if(p.unknownStarSignals>.15)goal='research';else goal=p.canTravel?'travel':'research';
 const decision=select(next,p,goal),skill=decision.skillId?SKILLS[decision.skillId]:null;
 if(skill){next.mana=Math.max(0,next.mana-skill.manaCost);next.cooldowns={...next.cooldowns,[skill.id]:p.worldSec+skill.cooldownSec};}
 return {state:{...next,goal,lastDecisionWorldSec:p.worldSec},decision};
}
/** Offline progression never simulates unseen kills, loot or damage; capped to 12 h. */
export function vaeltharOfflineProgress(m:VaeltharMind,elapsedSec:number):VaeltharMind{
 const dt=clamp(elapsedSec,0,VAELTHAR.mind.offlineMaxHours*3600);
 return {...m,worldSec:m.worldSec+dt,mana:clamp(m.mana+dt*.015,0,100),
  fatigue:clamp(m.fatigue-dt*.0002),researchProgress:Math.min(1e6,m.researchProgress+dt*.001)};
}
export function restoreVaeltharMind(data:unknown):VaeltharMind{
 if(!data||typeof data!=='object')return createVaeltharMind();
 const v=data as Partial<VaeltharMind>;if(v.schema!==1||v.characterId!==VAELTHAR_ID)return createVaeltharMind();
 const base=createVaeltharMind(v.worldSec);
 return {...base,...v,health:clamp(v.health??1),mana:clamp(v.mana??100,0,100),fatigue:clamp(v.fatigue??0),
  memories:Array.isArray(v.memories)?v.memories.slice(-VAELTHAR.mind.memoryMax):[],
  trust:typeof v.trust==='object'&&v.trust!==null?v.trust:{},
  discovered:typeof v.discovered==='object'&&v.discovered!==null?v.discovered:{},
  cooldowns:typeof v.cooldowns==='object'&&v.cooldowns!==null?v.cooldowns:{}};
}
